import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getToken } from "@/lib/api";

const ContentContext = createContext({
  content: {},
  isAdmin: false,
  setField: async () => {},
  ready: false,
});

export function ContentProvider({ children }) {
  const [content, setContent] = useState({});
  const [ready, setReady] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    api
      .get("/content")
      .then((r) => setContent(r.data || {}))
      .catch(() => setContent({}))
      .finally(() => setReady(true));
  }, []);

  // Detect admin session by calling verify on mount, on storage changes,
  // on a same-tab custom event after login, and when the tab regains focus.
  useEffect(() => {
    const check = () => {
      const token = getToken();
      if (!token) {
        setIsAdmin(false);
        return;
      }
      api
        .get("/admin/verify")
        .then(() => setIsAdmin(true))
        .catch(() => setIsAdmin(false));
    };
    check();
    window.addEventListener("storage", check);
    window.addEventListener("ark-auth-change", check);
    window.addEventListener("focus", check);
    return () => {
      window.removeEventListener("storage", check);
      window.removeEventListener("ark-auth-change", check);
      window.removeEventListener("focus", check);
    };
  }, []);

  const setField = useCallback(async (key, value) => {
    const { data } = await api.put("/admin/content", { fields: { [key]: value } });
    setContent(data || {});
  }, []);

  return (
    <ContentContext.Provider value={{ content, isAdmin, setField, ready }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  return useContext(ContentContext);
}

export function useText(key, fallback) {
  const { content } = useContent();
  const v = content[key];
  return v == null || v === "" ? fallback : v;
}

export function useImage(key, fallback) {
  const { content } = useContent();
  const v = content[key];
  return v == null || v === "" ? fallback : v;
}
